import React, { useState } from "react";
import axios from "axios";
import { MapPin, MessageCircle, Mail, Check, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, sent: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, sent: false, error: null });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, sent: true, error: null });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setStatus((s) => ({ ...s, sent: false })), 5000);
    } catch (err) {
      setStatus({ loading: false, sent: false, error: t("contact.formError") });
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="contact-page" className="pt-32 pb-20">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="overline mb-4">{t("contact.overline")}</p>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter text-white leading-[0.95] mb-8">
              {t("contact.title1")} <span className="italic text-[#C5A059]">{t("contact.title2")}</span>.
            </h1>
            <p className="text-[#A1A1AA] leading-relaxed mb-12 max-w-md">
              {t("contact.subtitle")}
            </p>

            <div className="space-y-8">
              <ContactBlock
                icon={<MapPin size={18} className="text-[#C5A059]" />}
                label={t("contact.address")}
                content={
                  <>
                    {t("contact.addressLine1")}
                    <br />
                    {t("contact.addressLine2")}
                    <br />
                    {t("contact.addressLine3")}
                  </>
                }
                testid="contact-address"
              />
              <ContactBlock
                icon={<MessageCircle size={18} className="text-[#C5A059]" />}
                label={t("contact.phone")}
                content={<a href="https://wa.me/243981962762" data-testid="contact-whatsapp-link" className="link-underline">+243 981 962 762</a>}
                testid="contact-phone"
              />
              <ContactBlock
                icon={<Mail size={18} className="text-[#C5A059]" />}
                label={t("contact.email")}
                content={
                  <div className="space-y-1.5">
                    <div>
                      <a href="mailto:contact@suzelholding.com" data-testid="contact-email-link" className="link-underline">
                        contact@suzelholding.com
                      </a>
                    </div>
                    <div>
                      <a href="mailto:ceo@suzelholding.com" data-testid="contact-ceo-email-link" className="link-underline text-white/70 text-sm">
                        ceo@suzelholding.com
                      </a>
                      <span className="text-[#C5A059] text-xs ml-2">· Direction</span>
                    </div>
                  </div>
                }
                testid="contact-email"
              />
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6 bg-[#0a0a0a] border border-white/5 p-8 md:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={`${t("contact.formName")} *`} required value={form.name} onChange={update("name")} testid="contact-input-name" />
                <Field label={`${t("contact.formEmail")} *`} type="email" required value={form.email} onChange={update("email")} testid="contact-input-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label={t("contact.formPhone")} value={form.phone} onChange={update("phone")} testid="contact-input-phone" />
                <Field label={`${t("contact.formSubject")} *`} required value={form.subject} onChange={update("subject")} testid="contact-input-subject" />
              </div>
              <div>
                <label className="overline block mb-3">{t("contact.formMessage")} *</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="w-full bg-transparent border-b border-white/10 focus:border-[#C5A059] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
                  placeholder={t("contact.formMessagePlaceholder")}
                />
              </div>

              {status.error && (
                <p data-testid="contact-error" className="text-sm text-red-400">{status.error}</p>
              )}
              {status.sent && (
                <div data-testid="contact-success" className="flex items-center gap-3 text-[#C5A059] text-sm">
                  <Check size={16} /> {t("contact.formSuccess")}
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={status.loading}
                  data-testid="contact-submit-button"
                  className="btn-gold w-full sm:w-auto justify-center"
                >
                  {status.loading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> {t("contact.formSubmitting")}
                    </>
                  ) : (
                    t("contact.formSubmit")
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactBlock = ({ icon, label, content, testid }) => (
  <div data-testid={testid} className="flex gap-4">
    <div className="pt-1">{icon}</div>
    <div>
      <p className="overline mb-1.5">{label}</p>
      <div className="text-white leading-relaxed">{content}</div>
    </div>
  </div>
);

const Field = ({ label, required, type = "text", value, onChange, testid }) => (
  <div>
    <label className="overline block mb-3">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/10 focus:border-[#C5A059] outline-none py-3 text-white placeholder:text-white/30 transition-colors"
    />
  </div>
);

export default Contact;
